import React, { useCallback, useEffect, useState } from 'react';
import {
	LOG_WORK_DATE_ERROR_KEY,
	LOG_WORK_DESCRIPTION_ERROR_KEY,
	LOG_WORK_TIME_REMAINING_ERROR_KEY,
	LOG_WORK_TIME_SPENT_ERROR_KEY,
	COMMON_LOG_WORK_ERROR_KEYS,
} from '@atlassian/jira-issue-create-common-types';
import { LogWorkField as CommonLogWorkField } from '@atlassian/jira-issue-create-commons';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import type { Props } from './types';

const LogWorkField = ({ additionalData, ...props }: Props) => {
	const { mediaContext, timeTrackingSettings } = additionalData;
	const [mediaUploadContext, setMediaUploadContext] = useState<unknown>(null);
	const [viewContext, setViewContext] = useState<unknown>(null);

	useEffect(() => {
		const uploadToken = mediaContext?.uploadToken;

		if (uploadToken) {
			const { clientId, endpointUrl, targetCollection, token, tokenDurationInMin } = uploadToken;
			const tokenIssueTimestamp = Date.now();
			const tokenLifespanInMs = tokenDurationInMin * 60 * 1000;

			setMediaUploadContext({
				clientId,
				serviceHost: endpointUrl,
				collection: targetCollection,
				tokenLifespanInMs,
				token,
				tokenIssueTimestamp,
			});
			setViewContext({
				clientId,
				serviceHost: endpointUrl,
				token,
				tokenLifespanInMs,
				areTokenPermissionsUpToDate: true,
				collection: targetCollection,
				tokenIssueTimestamp,
			});
		}
	}, [mediaContext?.uploadToken]);

	const mediaProvider = {
		userAuth: null,
		viewContext,
		uploadContext: mediaUploadContext,
	};

	const [
		{
			timeTrackingValue,
			meta: { submitFieldwiseErrors },
		},
	] = useFormContext();

	const getLogWorkFieldErrors = useCallback(() => {
		if (submitFieldwiseErrors) {
			const logWorkFieldWiseErrors: {
				[key: string]: string;
			} = {};
			[
				LOG_WORK_DATE_ERROR_KEY,
				LOG_WORK_DESCRIPTION_ERROR_KEY,
				LOG_WORK_TIME_REMAINING_ERROR_KEY,
				LOG_WORK_TIME_SPENT_ERROR_KEY,
			].forEach((errorKey) => {
				if (submitFieldwiseErrors[errorKey]) {
					logWorkFieldWiseErrors[errorKey] = submitFieldwiseErrors[errorKey];
				}
			});

			const logWorkOtherErrors: Array<string> = [];
			COMMON_LOG_WORK_ERROR_KEYS.forEach((key) => {
				if (submitFieldwiseErrors[key]) {
					logWorkOtherErrors.push(submitFieldwiseErrors[key]);
				}
			});
			return {
				...logWorkFieldWiseErrors,
				logWorkOtherErrors,
			};
		}
		return undefined;
	}, [submitFieldwiseErrors]);

	return (
		<CommonLogWorkField
			{...props}
			additionalData={{ timeTrackingSettings }}
			mediaContext={mediaProvider}
			timeTrackingValue={timeTrackingValue}
			errors={getLogWorkFieldErrors()}
		/>
	);
};

export default LogWorkField;
